import { useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import Button from '@rio-cloud/rio-uikit/Button';
import { mapTransactionsToRows, TransactionRow } from './mapTransactionsToRows';
import { RowData } from '../../../../components/table/Table';
import {
  PAYMENT_PARTNER_TO_TRANSLATION_KEY,
  SERVICE_TO_TRANSLATION_KEY,
  TRANSACTION_PRODUCT_TO_TRANSLATION_KEY,
  TRANSACTION_STATE_TO_TRANSLATION_KEY,
} from '../../../../i18n/enumMappings';
import {
  CustomFormattedMessage,
  dateFormat,
  TranslationsKeys,
  useCustomIntl,
} from '../../../../i18n/i18n';
import { getCurrencyDisplay } from '../../../../components/MonetaryValue';
import { useAppSelector } from '../../../../state/hooks';
import {
  getFromFilter,
  getServiceTypesFilter,
  getToFilter,
  getTransactionCurrenciesFilter,
  getVehicleGroupsFilter,
  getVehiclesFilter,
} from '../../../../state/transactions/transactionFilterSlice';
import { useTransactions } from '../../../../state/transactions/useTransactions';

const DOWNLOAD_FILENAME_SUFFIX = 'MAN_SimplePay_Transactions';

const formatMaskedCardNumber = (lastFourDigits: string) =>
  `***${lastFourDigits[0]} ${lastFourDigits.slice(1)}`;

export const CsvExportButton = () => {
  const [isExportingTransactions, setIsExportingTransactions] =
    useState<boolean>(false);

  const { formatDate, formatNumber } = useIntl();
  const intl = useCustomIntl();

  const vehicleGroups = useAppSelector(getVehicleGroupsFilter);
  const from = useAppSelector(getFromFilter);
  const to = useAppSelector(getToFilter);
  const transactionCurrencies = useAppSelector(getTransactionCurrenciesFilter);
  const filteredVehicleIds = useAppSelector(getVehiclesFilter);
  const serviceTypes = useAppSelector(getServiceTypesFilter);

  const { transactions } = useTransactions({
    vehicleId: filteredVehicleIds.length !== 0 ? filteredVehicleIds : undefined,
    vehicleGroupId: vehicleGroups.length !== 0 ? vehicleGroups : undefined,
    currency:
      transactionCurrencies.length !== 0 ? transactionCurrencies : undefined,
    fromDisplayDate: moment(from).format(),
    toDisplayDate: moment(to).format(),
    serviceType: serviceTypes.length !== 0 ? serviceTypes : undefined,
  });

  const getLabel = (id: TranslationsKeys) => {
    return intl.formatMessage({
      id,
    });
  };

  const convertToCsv = (transactionRows: RowData<TransactionRow>[]): string => {
    const headers: string[] = [
      getLabel('transactions.transactionsTable.header.vehicle'),
      getLabel('common.serviceType'),
      getLabel('common.transaction.product'),
      getLabel('transactions.transactionsTable.header.transactionDate'),
      getLabel('common.transaction.paymentPartner'),
      getLabel('common.card'),
      getLabel('common.transaction.totalPriceNet'),
      getLabel('common.transaction.totalPriceNetConverted'),
      getLabel('transactions.transactionsTable.header.status'),
    ];
    const tableData: string[][] = transactionRows.map(
      (transactionRow: RowData<TransactionRow>) => [
        transactionRow.vehicle?.name ??
          intl.formatMessage({ id: 'common.vehicle.unknown' }),
        getLabel(SERVICE_TO_TRANSLATION_KEY[transactionRow.service]),
        transactionRow.products
          .map((product) =>
            getLabel(TRANSACTION_PRODUCT_TO_TRANSLATION_KEY[product]),
          )
          .join(', '),
        formatDate(transactionRow.transactionDate, { ...dateFormat }),
        PAYMENT_PARTNER_TO_TRANSLATION_KEY[transactionRow.billingPartner],
        formatMaskedCardNumber(transactionRow.serviceCardLastFourDigits),
        !!transactionRow.totalPrice
          ? formatNumber(parseFloat(transactionRow.totalPrice.amountNet), {
              currency: transactionRow.totalPrice.currency,
              style: 'currency',
              currencyDisplay: getCurrencyDisplay(
                transactionRow.totalPrice.currency,
              ),
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : '',
        !!transactionRow.totalPriceConverted
          ? formatNumber(
              parseFloat(transactionRow.totalPriceConverted.amountNet),
              {
                currency: transactionRow.totalPriceConverted.currency,
                style: 'currency',
                currencyDisplay: getCurrencyDisplay(
                  transactionRow.totalPriceConverted.currency,
                ),
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )
          : '',
        getLabel(TRANSACTION_STATE_TO_TRANSLATION_KEY[transactionRow.state]),
      ],
    );

    const rows = [headers, ...tableData];

    return `${rows
      .map((row) =>
        row.map((cell) => `"${(cell ?? '').replace(/"/g, '""')}"`).join(';'),
      )
      .join('\n')}\n`;
  };

  const utf8Encode = (inputString: string): Uint8Array => {
    const textEncoder = new TextEncoder();
    return textEncoder.encode(inputString);
  };

  const triggerDownloadCsvFile = (
    fileContent: Uint8Array,
    filename: string,
  ) => {
    const blob = new Blob([fileContent as BlobPart], { type: 'text/csv' });
    const downloadLink = document.createElement('a');
    const href = window.URL.createObjectURL(blob);
    downloadLink.setAttribute('href', href);
    downloadLink.setAttribute('download', filename);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleExportTransactions = async () => {
    setIsExportingTransactions(true);
    try {
      const transactionRows = mapTransactionsToRows(transactions);
      const csvData: string = convertToCsv(transactionRows);
      const utf8CsvData: Uint8Array = utf8Encode(csvData);
      const formatedDatePrefix = moment().format('yyyyMMDD');
      const downloadFilename = `${formatedDatePrefix}_${DOWNLOAD_FILENAME_SUFFIX}.csv`;
      triggerDownloadCsvFile(utf8CsvData, downloadFilename);
    } catch (err) {
      console.error('Error exporting transactions table:', err);
    }
    setIsExportingTransactions(false);
  };

  const loadingClass = isExportingTransactions ? 'btn-loading' : '';

  return (
    <Button
      bsStyle={'primary'}
      className={loadingClass}
      disabled={isExportingTransactions}
      onClick={handleExportTransactions}
    >
      <span className={'rioglyph rioglyph-download'} />
      <CustomFormattedMessage
        id={'transactions.transactionsTable.toolbar.export'}
      />
    </Button>
  );
};
